import { Box, Button, DialogActions, DialogContent, Stack, styled, Typography } from "@mui/material";
import { useState } from "react";
import { isWhitespace, TextEditor } from "src/components";
import { useSession, useSignalR } from "src/hooks";
import { useCreateComment, useEditIssueDecision } from "src/http";
import { v4 as newGuid } from "uuid";
import { IssueClosedAnimation } from "../../assets/animations";
import { CommentSubject, ISSUE_STATUSES, IssueStatus } from "../../constants";

const AnimationContainer = styled(Box)`
  display: flex;
  height: 110px;
  width: 110px;
  padding: 5px;
  border-radius: 50%;
  background-color: white;
  align-self: center;
  position: relative;
  top: -50px;
  z-index: 2;
  border: 1px solid #cdcdcd;
`;

interface IProps {
  issueGuid: string;
  teamSlug?: string;
  createdByUserGuid: string;
  hasDecision: boolean;
  onDone: () => void;
}

const IssueProcessedDialogContent = (props: IProps) => {
  const session = useSession();
  const [text, setText] = useState<string>("");
  const { mutate: createComment, isLoading: isCreatingComment } = useCreateComment();
  const { mutate: editDecision, isLoading: isEditingDecision } = useEditIssueDecision();
  const signalR = useSignalR();

  const processedStatus = ISSUE_STATUSES.find((x) => x.id === IssueStatus.Processed)!;

  function handleDoneClicked() {
    if (props.hasDecision) {
      if (isWhitespace(text)) {
        props.onDone();
        return;
      }

      createComment(
        {
          subjectType: CommentSubject.Issue,
          subjectGuid: props.issueGuid,
          commentGuid: newGuid(),
          createdBy: {
            displayName: session.displayName,
            enabled: true,
            profilePhotoSmallUrl: session.profilePhotoSmallUrl,
            userId: session.userId,
          },
          text,
        },
        {
          onSuccess: () => {
            signalR.sendRefetchComments(props.issueGuid);
            props.onDone();
          },
        },
      );
    } else {
      editDecision(
        {
          issueGuid: props.issueGuid,
          decision: text,
          teamSlug: props.teamSlug,
          createdByUserId: props.createdByUserGuid,
          modifiedByUser: {
            userId: session.userId,
            displayName: session.displayName,
            enabled: true,
            profilePhotoSmallUrl: session.profilePhotoSmallUrl,
          },
        },
        {
          onSuccess: () => {
            props.onDone();
          },
        },
      );
    }
  }

  return (
    <>
      <AnimationContainer>
        <IssueClosedAnimation
          sx={{
            color: processedStatus.color,
            width: 100,
            height: 100,
          }}
        />
      </AnimationContainer>

      <DialogContent sx={{ mt: "-50px" }}>
        <Stack spacing={1}>
          <Typography variant="h5" sx={{ alignSelf: "center", textTransform: "uppercase" }}>
            {processedStatus.name.toUpperCase()}
          </Typography>

          <Stack spacing={1}>
            {props.hasDecision ? (
              <>
                <Typography>You've marked this Loop as PROCESSED. Add a comment (optional).</Typography>
                <Box>
                  <Typography variant="label">Comment</Typography>
                  <TextEditor placeholder="Type comment..." onChange={(value) => setText(value)} />
                </Box>
              </>
            ) : (
              <>
                <Typography variant="body2">Don't lose the decision.</Typography>
                <Typography>
                  This Loop is processed, but the decision is still missing. Document it so the outcome is visible to
                  your team and contributes to organizational learning.
                </Typography>
                <Box>
                  <Typography variant="label">Decision</Typography>
                  <TextEditor
                    placeholder="Enter the decision and the reasoning behind it..."
                    onChange={(value) => setText(value)}
                  />
                </Box>
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" disabled={isCreatingComment || isEditingDecision} onClick={handleDoneClicked}>
          Done
        </Button>
      </DialogActions>
    </>
  );
};

export { IssueProcessedDialogContent };
